import React, { useState, useEffect } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/Tabs';
import { EditableField } from '../ui/EditableField';
import { TagInput } from '../ui/TagInput';
import FileUpload from '../ui/FileUpload';
import ProfessionalPDFExtractionButton from '../pdf/ProfessionalPDFExtractionButton';
import { useFinancialInstitution } from '../../hooks/useFinancialInstitution';
import { CompanyLogo } from './CompanyLogo';
import { Edit3, Save, X, Loader2, Building2, Shield, FileText, Handshake, Send } from 'lucide-react';
import {
  FINANCIAL_INSTITUTION_TYPES,
  FINANCIAL_INSTITUTION_SUBTYPES,
  SUPERVISORY_AUTHORITIES,
  CLIENT_SEGMENTS,
  LEGAL_STATUSES,
  PARTNERSHIP_MOTIVATIONS,
  CURRENCIES,
} from '../../constants/financialInstitutionOptions';

interface FinancialInstitutionFormProps {
  financialInstitutionId?: string;
}

const FinancialInstitutionForm: React.FC<FinancialInstitutionFormProps> = ({ financialInstitutionId }) => {
  // ✨ Utiliser le hook dédié aux institutions financières
  const testInstitutionId = financialInstitutionId || 'inst-001';
  const { institution, updateInstitutionData, loading } = useFinancialInstitution(testInstitutionId);
  
  // État local pour le formulaire
  const [formData, setFormData] = useState({
    // Identification institutionnelle
    denominationSociale: '',
    sigle: '',
    typeInstitution: '',
    sousCategorie: '',
    dateCreation: '',
    paysOrigine: 'RDC',
    statutJuridique: '',
    
    // Informations réglementaires
    autoritéSupervision: '',
    numeroAgrement: '',
    dateAgrement: '',
    validiteAgrement: '',
    numeroRCCM: '',
    numeroNIF: '',
    
    // Activités autorisées
    activitesAutorisees: [] as string[],
    
    // Informations opérationnelles
    siegeSocial: '',
    nombreAgences: 0,
    villesProvincesCouvertes: [] as string[],
    presenceInternationale: false,
    
    // Capacités financières
    capitalSocialMinimum: '',
    capitalSocialActuel: '',
    fondsPropresMontant: '',
    totalBilan: '',
    chiffreAffairesAnnuel: '',
    devise: 'USD' as 'USD' | 'CDF' | 'EUR',
    
    // Clientèle et marché
    segmentClientelePrincipal: '',
    nombreClientsActifs: 0,
    portefeuilleCredit: '',
    depotsCollectes: '',
    
    // Services offerts à Wanzo
    servicesCredit: [] as string[],
    servicesInvestissement: [] as string[],
    servicesGarantie: [] as string[],
    servicesTransactionnels: [] as string[],
    servicesConseil: [] as string[],
    
    // Partenariat Wanzo
    motivationPrincipale: '',
    servicesPrioritaires: [] as string[],
    segmentsClienteleCibles: [] as string[],
    volumeAffairesEnvisage: '',
    
    // Conditions commerciales
    grillesTarifaires: '',
    conditionsPreferentielles: '',
    delaisTraitement: '',
    criteresEligibilite: '',
    
    // Capacité d'engagement
    montantMaximumDossier: '',
    enveloppeGlobale: '',
    secteursActivitePrivilegies: [] as string[],
    zonesGeographiquesPrioritaires: [] as string[],
    
    // Documents
    documentsLegaux: [] as any[],
    documentsFinanciers: [] as any[],
    documentsOperationnels: [] as any[],
    documentsCompliance: [] as any[],
  });

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Synchroniser avec les données de l'institution
  useEffect(() => {
    if (institution) {
      setFormData(prev => ({
        ...prev,
        denominationSociale: institution.denominationSociale || '',
        sigle: institution.sigle || '',
        typeInstitution: institution.typeInstitution || '',
        // Mapper toutes les autres données depuis l'institution
      }));
    }
  }, [institution]);

  const handleStartEditing = () => {
    setIsEditing(true);
  };

  const handleCancelEditing = () => {
    setIsEditing(false);
    // Restaurer les données originales
    if (institution) {
      setFormData(prev => ({
        ...prev,
        denominationSociale: institution.denominationSociale || '',
      }));
    }
  };

  const handleSaveProfile = async () => {
    if (!isEditing) return;
    
    setIsSaving(true);
    try {
      // ✨ NOUVEAU: Transmettre TOUTES les données du formulaire
      // Le formulaire est la source de vérité, on envoie tout directement
      await updateInstitutionData({
        // ============================================
        // SECTION: Identification Institutionnelle
        // ============================================
        denominationSociale: formData.denominationSociale,
        sigle: formData.sigle,
        typeInstitution: formData.typeInstitution,
        sousCategorie: formData.sousCategorie,
        dateCreation: formData.dateCreation,
        paysOrigine: formData.paysOrigine,
        statutJuridique: formData.statutJuridique,
        
        // ============================================
        // SECTION: Informations Réglementaires
        // ============================================
        autoritéSupervision: formData.autoritéSupervision,
        numeroAgrement: formData.numeroAgrement,
        dateAgrement: formData.dateAgrement,
        validiteAgrement: formData.validiteAgrement,
        numeroRCCM: formData.numeroRCCM,
        numeroNIF: formData.numeroNIF,
        
        // ============================================
        // SECTION: Activités Autorisées
        // ============================================
        activitesAutorisees: formData.activitesAutorisees,
        
        // ============================================
        // SECTION: Informations Opérationnelles
        // ============================================
        siegeSocial: formData.siegeSocial,
        nombreAgences: formData.nombreAgences,
        villesProvincesCouvertes: formData.villesProvincesCouvertes,
        presenceInternationale: formData.presenceInternationale,
        
        // ============================================
        // SECTION: Capacités Financières
        // ============================================
        capitalSocialMinimum: formData.capitalSocialMinimum,
        capitalSocialActuel: formData.capitalSocialActuel,
        fondsPropresMontant: formData.fondsPropresMontant,
        totalBilan: formData.totalBilan,
        chiffreAffairesAnnuel: formData.chiffreAffairesAnnuel,
        devise: formData.devise,
        
        // ============================================
        // SECTION: Clientèle et Marché
        // ============================================
        segmentClientelePrincipal: formData.segmentClientelePrincipal,
        nombreClientsActifs: formData.nombreClientsActifs,
        portefeuilleCredit: formData.portefeuilleCredit,
        depotsCollectes: formData.depotsCollectes,
        
        // ============================================
        // SECTION: Services Offerts à Wanzo
        // ============================================
        servicesCredit: formData.servicesCredit,
        servicesInvestissement: formData.servicesInvestissement,
        servicesGarantie: formData.servicesGarantie,
        servicesTransactionnels: formData.servicesTransactionnels,
        servicesConseil: formData.servicesConseil,
        
        // ============================================
        // SECTION: Partenariat Wanzo
        // ============================================
        motivationPrincipale: formData.motivationPrincipale,
        servicesPrioritaires: formData.servicesPrioritaires,
        segmentsClienteleCibles: formData.segmentsClienteleCibles,
        volumeAffairesEnvisage: formData.volumeAffairesEnvisage,
        
        // ============================================
        // SECTION: Conditions Commerciales
        // ============================================
        grillesTarifaires: formData.grillesTarifaires,
        conditionsPreferentielles: formData.conditionsPreferentielles,
        delaisTraitement: formData.delaisTraitement,
        criteresEligibilite: formData.criteresEligibilite,
        
        // ============================================
        // SECTION: Capacité d'Engagement
        // ============================================
        montantMaximumDossier: formData.montantMaximumDossier,
        enveloppeGlobale: formData.enveloppeGlobale,
        secteursActivitePrivilegies: formData.secteursActivitePrivilegies,
        zonesGeographiquesPrioritaires: formData.zonesGeographiquesPrioritaires,
        
        // ============================================
        // SECTION: Documents Obligatoires
        // ============================================
        documentsLegaux: formData.documentsLegaux,
        documentsFinanciers: formData.documentsFinanciers,
        documentsOperationnels: formData.documentsOperationnels,
        documentsCompliance: formData.documentsCompliance,
      });
      setIsEditing(false);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmitProfile = async () => {
    if (!isEditing) return;
    
    setIsSubmitting(true);
    try {
      // ✨ NOUVEAU: Transmettre TOUTES les données + marquer comme soumis
      await updateInstitutionData({
        // ============================================
        // TOUTES LES DONNÉES DU FORMULAIRE
        // ============================================
        denominationSociale: formData.denominationSociale,
        sigle: formData.sigle,
        typeInstitution: formData.typeInstitution,
        sousCategorie: formData.sousCategorie,
        dateCreation: formData.dateCreation,
        paysOrigine: formData.paysOrigine,
        statutJuridique: formData.statutJuridique,
        autoritéSupervision: formData.autoritéSupervision,
        numeroAgrement: formData.numeroAgrement,
        dateAgrement: formData.dateAgrement,
        validiteAgrement: formData.validiteAgrement,
        numeroRCCM: formData.numeroRCCM,
        numeroNIF: formData.numeroNIF,
        activitesAutorisees: formData.activitesAutorisees,
        siegeSocial: formData.siegeSocial,
        nombreAgences: formData.nombreAgences,
        villesProvincesCouvertes: formData.villesProvincesCouvertes,
        presenceInternationale: formData.presenceInternationale,
        capitalSocialMinimum: formData.capitalSocialMinimum,
        capitalSocialActuel: formData.capitalSocialActuel,
        fondsPropresMontant: formData.fondsPropresMontant,
        totalBilan: formData.totalBilan,
        chiffreAffairesAnnuel: formData.chiffreAffairesAnnuel,
        devise: formData.devise,
        segmentClientelePrincipal: formData.segmentClientelePrincipal,
        nombreClientsActifs: formData.nombreClientsActifs,
        portefeuilleCredit: formData.portefeuilleCredit,
        depotsCollectes: formData.depotsCollectes,
        servicesCredit: formData.servicesCredit,
        servicesInvestissement: formData.servicesInvestissement,
        servicesGarantie: formData.servicesGarantie,
        servicesTransactionnels: formData.servicesTransactionnels,
        servicesConseil: formData.servicesConseil,
        motivationPrincipale: formData.motivationPrincipale,
        servicesPrioritaires: formData.servicesPrioritaires,
        segmentsClienteleCibles: formData.segmentsClienteleCibles,
        volumeAffairesEnvisage: formData.volumeAffairesEnvisage,
        grillesTarifaires: formData.grillesTarifaires,
        conditionsPreferentielles: formData.conditionsPreferentielles,
        delaisTraitement: formData.delaisTraitement,
        criteresEligibilite: formData.criteresEligibilite,
        montantMaximumDossier: formData.montantMaximumDossier,
        enveloppeGlobale: formData.enveloppeGlobale,
        secteursActivitePrivilegies: formData.secteursActivitePrivilegies,
        zonesGeographiquesPrioritaires: formData.zonesGeographiquesPrioritaires,
        documentsLegaux: formData.documentsLegaux,
        documentsFinanciers: formData.documentsFinanciers,
        documentsOperationnels: formData.documentsOperationnels,
        documentsCompliance: formData.documentsCompliance,
      });
      setIsEditing(false);
      
      console.log('✅ Profil institution financière soumis pour validation par l\'administration Wanzo');
      // TODO: Implémenter le système de statut de soumission pour les institutions financières
      
    } catch (error) {
      console.error('Erreur lors de la soumission:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFieldChange = async (field: string, value: any) => {
    if (!isEditing) return;
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const getSousCategorieOptions = () => {
    if (formData.typeInstitution && formData.typeInstitution in FINANCIAL_INSTITUTION_SUBTYPES) {
      return FINANCIAL_INSTITUTION_SUBTYPES[formData.typeInstitution as keyof typeof FINANCIAL_INSTITUTION_SUBTYPES];
    }
    return [];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-[#197ca8]" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6">
      {/* En-tête */}
      <div className="flex flex-col space-y-4 lg:flex-row lg:items-center lg:justify-between lg:space-y-0 mb-6 pb-4 border-b border-gray-200">
        <div className="min-w-0 flex-1">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center">
            <Building2 className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3 text-[#197ca8]" />
            <span className="break-words">Identification Institution Financière</span>
          </h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">
            Formulaire de partenariat pour institutions financières - Informations simplifiées
          </p>
        </div>
        
        <div className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
          {!isEditing ? (
            <button
              onClick={handleStartEditing}
              className="inline-flex items-center px-3 py-2 sm:px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#197ca8] hover:bg-[#156a92] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#197ca8] transition-colors"
            >
              <Edit3 className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Éditer le profil</span>
              <span className="sm:hidden">Éditer</span>
            </button>
          ) : (
            <>
              <button
                onClick={handleCancelEditing}
                disabled={isSaving || isSubmitting}
                className="inline-flex items-center px-3 py-2 sm:px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#197ca8] transition-colors disabled:opacity-50"
              >
                <X className="w-4 h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Annuler</span>
              </button>
              <button
                onClick={handleSaveProfile}
                disabled={isSaving || isSubmitting}
                className="inline-flex items-center px-3 py-2 sm:px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors disabled:opacity-50"
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 mr-1 sm:mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-1 sm:mr-2" />
                )}
                {isSaving ? 'Sauvegarde...' : 'Sauvegarder'}
              </button>
              <button
                onClick={handleSubmitProfile}
                disabled={isSaving || isSubmitting}
                className="inline-flex items-center px-3 py-2 sm:px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-50"
              >
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 mr-1 sm:mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-1 sm:mr-2" />
                )}
                {isSubmitting ? 'Soumission...' : 'Soumettre'}
              </button>
            </>
          )}
        </div>
      </div>

      <Tabs defaultValue="identification">
        <TabsList className="mb-8">
          <TabsTrigger value="identification">
            <Building2 className="w-4 h-4 mr-2" />
            Identification
          </TabsTrigger>
          <TabsTrigger value="reglementaire">
            <Shield className="w-4 h-4 mr-2" />
            Réglementaire
          </TabsTrigger>
          <TabsTrigger value="partenariat">
            <Handshake className="w-4 h-4 mr-2" />
            Partenariat
          </TabsTrigger>
          <TabsTrigger value="documents">
            <FileText className="w-4 h-4 mr-2" />
            Documents
          </TabsTrigger>
        </TabsList>

        {/* Section Identification */}
        <TabsContent value="identification">
          <div className="space-y-8">
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-6">
                Informations de Base
              </h2>
              
              {/* Logo de l'institution */}
              <div className="mb-6 pb-6 border-b border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Logo de l'institution financière
                </label>
                <CompanyLogo
                  companyId={institution?.id || 'temp-id'}
                  logo={undefined}
                  name={formData.denominationSociale || 'Votre Institution'}
                  onLogoUpdate={(success) => {
                    if (success) {
                      console.log('Logo mis à jour avec succès');
                      // TODO: Mettre à jour le logo dans l'institution
                    }
                  }}
                />
              </div>
              
              <div className="space-y-6">
                <EditableField
                  label="Dénomination sociale complète *"
                  value={formData.denominationSociale}
                  onSave={async (value) => {
                    if (!isEditing) return;
                    setFormData(prev => ({ ...prev, denominationSociale: value.toString() }));
                  }}
                  disabled={!isEditing}
                  required
                />

                <EditableField
                  label="Sigle ou nom commercial"
                  value={formData.sigle}
                  onSave={async (value) => {
                    if (!isEditing) return;
                    setFormData(prev => ({ ...prev, sigle: value.toString() }));
                  }}
                  disabled={!isEditing}
                />

                <EditableField
                  label="Type d'institution *"
                  value={formData.typeInstitution}
                  onSave={async (value) => {
                    if (!isEditing) return;
                    setFormData(prev => ({ ...prev, typeInstitution: value.toString(), sousCategorie: '' }));
                  }}
                  type="select"
                  options={FINANCIAL_INSTITUTION_TYPES as any}
                  disabled={!isEditing}
                  required
                />

                {formData.typeInstitution && (
                  <EditableField
                    label="Sous-catégorie spécifique *"
                    value={formData.sousCategorie}
                    onSave={async (value) => {
                      if (!isEditing) return;
                      setFormData(prev => ({ ...prev, sousCategorie: value.toString() }));
                    }}
                    type="select"
                    options={getSousCategorieOptions() as any}
                    disabled={!isEditing}
                    required
                  />
                )}

                <EditableField
                  label="Date de création/agrément"
                  value={formData.dateCreation}
                  onSave={async (value) => {
                    if (!isEditing) return;
                    setFormData(prev => ({ ...prev, dateCreation: value.toString() }));
                  }}
                  type="date"
                  disabled={!isEditing}
                />

                <EditableField
                  label="Statut juridique"
                  value={formData.statutJuridique}
                  onSave={async (value) => {
                    if (!isEditing) return;
                    setFormData(prev => ({ ...prev, statutJuridique: value.toString() }));
                  }}
                  type="select"
                  options={LEGAL_STATUSES as any}
                  disabled={!isEditing}
                />
              </div>
            </section>

            {/* Informations opérationnelles */}
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-6">
                Informations Opérationnelles
              </h2>
              <div className="space-y-6">
                <EditableField
                  label="Adresse du siège social *"
                  value={formData.siegeSocial}
                  onSave={async (value) => {
                    if (!isEditing) return;
                    setFormData(prev => ({ ...prev, siegeSocial: value.toString() }));
                  }}
                  type="textarea"
                  disabled={!isEditing}
                  required
                />

                <EditableField
                  label="Nombre d'agences/succursales"
                  value={formData.nombreAgences.toString()}
                  onSave={async (value) => {
                    if (!isEditing) return;
                    setFormData(prev => ({ ...prev, nombreAgences: parseInt(value.toString()) || 0 }));
                  }}
                  type="number"
                  disabled={!isEditing}
                />

                <TagInput
                  label="Villes/Provinces couvertes"
                  value={formData.villesProvincesCouvertes}
                  onSave={(tags) => handleFieldChange('villesProvincesCouvertes', tags)}
                  disabled={!isEditing}
                  placeholder="Ex: Kinshasa, Goma, Lubumbashi..."
                />

                <EditableField
                  label="Segment de clientèle principal"
                  value={formData.segmentClientelePrincipal}
                  onSave={async (value) => {
                    if (!isEditing) return;
                    setFormData(prev => ({ ...prev, segmentClientelePrincipal: value.toString() }));
                  }}
                  type="select"
                  options={CLIENT_SEGMENTS as any}
                  disabled={!isEditing}
                />

                <EditableField
                  label="Nombre de clients actifs"
                  value={formData.nombreClientsActifs.toString()}
                  onSave={async (value) => {
                    if (!isEditing) return;
                    setFormData(prev => ({ ...prev, nombreClientsActifs: parseInt(value.toString()) || 0 }));
                  }}
                  type="number"
                  disabled={!isEditing}
                />
              </div>
            </section>
          </div>
        </TabsContent>

        {/* Section Réglementaire */}
        <TabsContent value="reglementaire">
          <div className="space-y-8">
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-6">
                Informations Réglementaires
              </h2>
              <div className="space-y-6">
                <EditableField
                  label="Autorité de supervision"
                  value={formData.autoritéSupervision}
                  onSave={async (value) => {
                    if (!isEditing) return;
                    setFormData(prev => ({ ...prev, autoritéSupervision: value.toString() }));
                  }}
                  type="select"
                  options={SUPERVISORY_AUTHORITIES as any}
                  disabled={!isEditing}
                />

                <EditableField
                  label="Numéro d'agrément/licence"
                  value={formData.numeroAgrement}
                  onSave={async (value) => {
                    if (!isEditing) return;
                    setFormData(prev => ({ ...prev, numeroAgrement: value.toString() }));
                  }}
                  disabled={!isEditing}
                />

                <EditableField
                  label="Date d'obtention de l'agrément"
                  value={formData.dateAgrement}
                  onSave={async (value) => {
                    if (!isEditing) return;
                    setFormData(prev => ({ ...prev, dateAgrement: value.toString() }));
                  }}
                  type="date"
                  disabled={!isEditing}
                />

                <EditableField
                  label="Numéro RCCM"
                  value={formData.numeroRCCM}
                  onSave={async (value) => {
                    if (!isEditing) return;
                    setFormData(prev => ({ ...prev, numeroRCCM: value.toString() }));
                  }}
                  disabled={!isEditing}
                />

                <EditableField
                  label="Numéro d'identification fiscale (NIF)"
                  value={formData.numeroNIF}
                  onSave={async (value) => {
                    if (!isEditing) return;
                    setFormData(prev => ({ ...prev, numeroNIF: value.toString() }));
                  }}
                  disabled={!isEditing}
                />
              </div>
            </section>

            {/* Activités autorisées */}
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-6">
                Activités Autorisées
              </h2>
              <TagInput
                label="Services autorisés par votre agrément"
                value={formData.activitesAutorisees}
                onSave={(tags) => handleFieldChange('activitesAutorisees', tags)}
                disabled={!isEditing}
                placeholder="Sélectionnez vos activités autorisées..."
              />
            </section>

            {/* Capacités financières */}
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-6">
                Capacités Financières
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <EditableField
                  label="Capital social actuel"
                  value={formData.capitalSocialActuel}
                  onSave={async (value) => {
                    if (!isEditing) return;
                    setFormData(prev => ({ ...prev, capitalSocialActuel: value.toString() }));
                  }}
                  type="number"
                  disabled={!isEditing}
                />

                <EditableField
                  label="Devise de référence"
                  value={formData.devise}
                  onSave={async (value) => {
                    if (!isEditing) return;
                    setFormData(prev => ({ ...prev, devise: value as 'USD' | 'CDF' | 'EUR' }));
                  }}
                  type="select"
                  options={CURRENCIES as any}
                  disabled={!isEditing}
                />

                <EditableField
                  label="Fonds propres"
                  value={formData.fondsPropresMontant}
                  onSave={async (value) => {
                    if (!isEditing) return;
                    setFormData(prev => ({ ...prev, fondsPropresMontant: value.toString() }));
                  }}
                  type="number"
                  disabled={!isEditing}
                />

                <EditableField
                  label="Chiffre d'affaires annuel"
                  value={formData.chiffreAffairesAnnuel}
                  onSave={async (value) => {
                    if (!isEditing) return;
                    setFormData(prev => ({ ...prev, chiffreAffairesAnnuel: value.toString() }));
                  }}
                  type="number"
                  disabled={!isEditing}
                />
              </div>
            </section>
          </div>
        </TabsContent>

        {/* Section Partenariat */}
        <TabsContent value="partenariat">
          <div className="space-y-8">
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-6">
                Services offerts à l'écosystème Wanzo
              </h2>
              <div className="space-y-6">
                <TagInput
                  label="Services de crédit"
                  value={formData.servicesCredit}
                  onSave={(tags) => handleFieldChange('servicesCredit', tags)}
                  disabled={!isEditing}
                />

                <TagInput
                  label="Services d'investissement"
                  value={formData.servicesInvestissement}
                  onSave={(tags) => handleFieldChange('servicesInvestissement', tags)}
                  disabled={!isEditing}
                />

                <TagInput
                  label="Services de garantie"
                  value={formData.servicesGarantie}
                  onSave={(tags) => handleFieldChange('servicesGarantie', tags)}
                  disabled={!isEditing}
                />

                <TagInput
                  label="Services transactionnels"
                  value={formData.servicesTransactionnels}
                  onSave={(tags) => handleFieldChange('servicesTransactionnels', tags)}
                  disabled={!isEditing}
                />

                <TagInput
                  label="Services de conseil"
                  value={formData.servicesConseil}
                  onSave={(tags) => handleFieldChange('servicesConseil', tags)}
                  disabled={!isEditing}
                />
              </div>
            </section>

            {/* Conditions de partenariat */}
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-6">
                Conditions de Partenariat
              </h2>
              <div className="space-y-6">
                <EditableField
                  label="Motivation principale pour rejoindre Wanzo"
                  value={formData.motivationPrincipale}
                  onSave={async (value) => {
                    if (!isEditing) return;
                    setFormData(prev => ({ ...prev, motivationPrincipale: value.toString() }));
                  }}
                  type="select"
                  options={PARTNERSHIP_MOTIVATIONS as any}
                  disabled={!isEditing}
                />

                <EditableField
                  label="Volume d'affaires envisagé (annuel)"
                  value={formData.volumeAffairesEnvisage}
                  onSave={async (value) => {
                    if (!isEditing) return;
                    setFormData(prev => ({ ...prev, volumeAffairesEnvisage: value.toString() }));
                  }}
                  type="number"
                  disabled={!isEditing}
                />

                <EditableField
                  label="Montant maximum par dossier"
                  value={formData.montantMaximumDossier}
                  onSave={async (value) => {
                    if (!isEditing) return;
                    setFormData(prev => ({ ...prev, montantMaximumDossier: value.toString() }));
                  }}
                  type="number"
                  disabled={!isEditing}
                />

                <EditableField
                  label="Enveloppe globale disponible"
                  value={formData.enveloppeGlobale}
                  onSave={async (value) => {
                    if (!isEditing) return;
                    setFormData(prev => ({ ...prev, enveloppeGlobale: value.toString() }));
                  }}
                  type="number"
                  disabled={!isEditing}
                />

                <EditableField
                  label="Délais de traitement des dossiers (en jours)"
                  value={formData.delaisTraitement}
                  onSave={async (value) => {
                    if (!isEditing) return;
                    setFormData(prev => ({ ...prev, delaisTraitement: value.toString() }));
                  }}
                  type="number"
                  disabled={!isEditing}
                />
              </div>
            </section>
          </div>
        </TabsContent>

        {/* Section Documents */}
        <TabsContent value="documents">
          <div className="space-y-8">
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-6">
                Documents Obligatoires
              </h2>
              
              <div className="space-y-8">
                <FileUpload
                  label="Documents Légaux et Réglementaires"
                  description="Statuts, agrément, RCCM, NIF, attestations de conformité"
                  value={formData.documentsLegaux}
                  onSave={(files) => handleFieldChange('documentsLegaux', files)}
                  disabled={!isEditing}
                  multiple={true}
                  acceptedTypes={['.pdf', '.doc', '.docx', '.jpg', '.png']}
                  maxSize={10}
                />

                <FileUpload
                  label="Documents Financiers"
                  description="États financiers audités (3 derniers exercices), rapport annuel, notation"
                  value={formData.documentsFinanciers}
                  onSave={(files) => handleFieldChange('documentsFinanciers', files)}
                  disabled={!isEditing}
                  multiple={true}
                  acceptedTypes={['.pdf', '.doc', '.docx', '.xls', '.xlsx']}
                  maxSize={20}
                />

                <FileUpload
                  label="Documents Opérationnels"
                  description="Organigramme, CV dirigeants, présentation institutionnelle, grille tarifaire"
                  value={formData.documentsOperationnels}
                  onSave={(files) => handleFieldChange('documentsOperationnels', files)}
                  disabled={!isEditing}
                  multiple={true}
                  acceptedTypes={['.pdf', '.doc', '.docx', '.ppt', '.pptx']}
                  maxSize={15}
                />

                <FileUpload
                  label="Documents de Compliance"
                  description="Politique KYC/AML, procédures de contrôle interne, certifications"
                  value={formData.documentsCompliance}
                  onSave={(files) => handleFieldChange('documentsCompliance', files)}
                  disabled={!isEditing}
                  multiple={true}
                  acceptedTypes={['.pdf', '.doc', '.docx']}
                  maxSize={10}
                />
              </div>
            </section>
          </div>
        </TabsContent>
      </Tabs>

      {/* Section d'extraction PDF */}
      <div className="mt-8 pt-6 border-t border-gray-200">
        <div className="bg-gray-50 rounded-lg p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div className="mb-4 sm:mb-0">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Extraction du formulaire
              </h3>
              <p className="text-sm text-gray-600">
                Téléchargez votre formulaire d'identification au format PDF pour archivage ou soumission externe.
              </p>
            </div>
            <div className="flex-shrink-0">
              <ProfessionalPDFExtractionButton
                type="financial-institution"
                formData={formData}
                entityName={formData.denominationSociale || institution?.denominationSociale || "Mon institution"}
                disabled={false}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialInstitutionForm;